import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
  Alert,
  Checkbox,
  Switch,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  TextField,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FolderIcon from '@mui/icons-material/Folder';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Tree, NodeRendererProps, TreeApi } from 'react-arborist';
import DashboardLayout from '../components/DashboardLayout';
import { FormNavigationProvider, NavTextField, NavCheckbox, NavButton } from '../components/FormNavigation';
import NavBanglaPhoneticInput from '../components/NavBanglaPhoneticInput';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchEmployeeTags,
  addEmployeeTag,
  replaceEmployeeTag,
  removeEmployeeTag,
} from '../store/employeeTagsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { EmployeeTag, CreateEmployeeTagPayload, PatchEmployeeTagPayload, SetTagHistoryPayload, UnlockEmployeeTagPayload } from '../types/employeeTags';

// ─── Tree builder ─────────────────────────────────────────────────────────────

interface TagNode {
  id: string;
  name: string;
  tag: EmployeeTag;
  children?: TagNode[];
}

function buildTree(tags: EmployeeTag[]): TagNode[] {
  const map = new Map<number, TagNode>();
  const roots: TagNode[] = [];
  
  for (const tag of tags) {
    map.set(tag.tagId, { id: String(tag.tagId), name: tag.tagTitle, tag, children: [] });
  }
  
  for (const tag of tags) {
    const node = map.get(tag.tagId)!;
    if (tag.parentId === 0) {
      roots.push(node);
    } else {
      map.get(tag.parentId)?.children?.push(node);
    }
  }

  // Preserve children: [] arrays so react-arborist treats ALL tags as potential valid 'folders'
  // for drag-and-drop receiving, instead of un-droppable static files.
  
  return roots;
}

// ─── Callbacks passed through tree (avoids prop-drilling on every field) ─────

interface TreeCallbacks {
  onUnlock: (tag: EmployeeTag) => void;
  onInfo: (tag: EmployeeTag) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, tag: EmployeeTag) => void;
  onContextMenu: (event: React.MouseEvent<HTMLElement>, tag: EmployeeTag) => void;
  onHistoryEdit: (tag: EmployeeTag) => void;
  unlocking: number | null;
}

// ─── Tree item renderer ───────────────────────────────────────────────────────

function TagNodeRenderer({ node, style, tree, dragHandle }: NodeRendererProps<TagNode>) {
  const tag = node.data.tag;
  const isRoot = tag.parentId === 0;
  // Node holds children if the explicit array has contents.
  const hasChildren = node.data.children && node.data.children.length > 0;
  const cb = (tree.props as any).callbacks as TreeCallbacks;

  const typeIcon = isRoot
    ? <AccountTreeIcon sx={{ fontSize: 15, color: tag.locked ? '#546e7a' : 'primary.main', flexShrink: 0 }} />
    : hasChildren
      ? <FolderIcon sx={{ fontSize: 15, color: tag.locked ? '#546e7a' : 'text.secondary', flexShrink: 0 }} />
      : <FiberManualRecordIcon sx={{ fontSize: 8, color: tag.locked ? '#546e7a' : 'text.disabled', flexShrink: 0 }} />;

  return (
    <Box
      ref={dragHandle}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.25,
        pr: 0.5,
        gap: 1,
        minWidth: 0,
        cursor: 'pointer',
        border: '1px solid transparent',
        borderColor: node.willReceiveDrop ? 'rgba(25, 118, 210, 0.3)' : 'transparent',
        bgcolor: node.willReceiveDrop ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
        '&:hover': { bgcolor: node.willReceiveDrop ? 'rgba(25, 118, 210, 0.1)' : 'action.hover' },
        ...(node.isSelected && !node.willReceiveDrop && { bgcolor: 'action.selected' }),
        borderRadius: 1,
      }}
      onClick={() => node.toggle()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        node.select();
        cb.onContextMenu(e, tag);
      }}
    >
      {/* Indentation logic using node.level */}
      <Box sx={{ width: node.level * 20, flexShrink: 0 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
        }}
      >
        {hasChildren && (
          <ChevronRightIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              transition: 'transform 0.2s',
              transform: node.isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </Box>

      {typeIcon}

      {/* title */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body1"
          component="div"
          sx={{
            userSelect: 'none',
            fontSize: '1rem',
            fontWeight: isRoot ? 600 : 400,
          }}
          noWrap
        >
          {tag.tagTitle}
          {tag.tagTitleBangla && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {tag.tagTitleBangla}
            </Typography>
          )}
        </Typography>
        {tag.locked && (
          <LockIcon sx={{ fontSize: 13, color: '#e57373', flexShrink: 0 }} />
        )}
      </Box>

      {/* action buttons */}
      <Box
        sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {tag.locked && (
          <Tooltip title="Unlock">
            <IconButton
              size="small"
              onClick={() => cb.onUnlock(tag)}
              disabled={cb.unlocking === tag.tagId}
            >
              {cb.unlocking === tag.tagId
                ? <CircularProgress size={14} />
                : <LockOutlinedIcon sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </Tooltip>
        )}
        {isRoot && (
          <Tooltip title={tag.historyEnabled ? 'History is enabled' : 'History is disabled'}>
            <IconButton size="small" onClick={() => cb.onHistoryEdit(tag)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={tag.historyEnabled ? '#66bb6a' : '#9e9e9e'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l4 2" />
              </svg>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Info">
          <IconButton size="small" onClick={() => cb.onInfo(tag)}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={(e) => cb.onMenuOpen(e, tag)}>
          <MoreVertIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Info modal ───────────────────────────────────────────────────────────────

function InfoModal({ tag, onClose }: { tag: EmployeeTag | null; onClose: () => void }) {
  if (!tag) return null;
  const isRoot = tag.parentId === 0;
  const rows: Array<[string, React.ReactNode]> = [
    ['ID', tag.tagId],
    ['Title', tag.tagTitle],
    ...(tag.tagTitleBangla ? [['Title (Bangla)', tag.tagTitleBangla] as [string, React.ReactNode]] : []),
    ...(tag.tagCode ? [['Code', tag.tagCode] as [string, React.ReactNode]] : []),
    ['Type', isRoot ? 'Root' : 'Child'],
    ...(!isRoot ? [['Parent ID', tag.parentId] as [string, React.ReactNode]] : []),
    ...(tag.fullTagTitle ? [['Full Title', tag.fullTagTitle] as [string, React.ReactNode]] : []),
    ...(isRoot ? [
      ['Allow Multi Selection', tag.allowMultiChildSelection ? 'Yes' : 'No'] as [string, React.ReactNode],
      ['Consider Sub-Parent as Tag', tag.considerSubParentAsTag ? 'Yes' : 'No'] as [string, React.ReactNode],
      ['Multi-Level Grouping', tag.multiLevelGrouping ? 'Yes' : 'No'] as [string, React.ReactNode],
      ['Concat Titles', tag.concatTitles ? 'Yes' : 'No'] as [string, React.ReactNode],
      ['History Tracking', tag.historyEnabled ? 'Yes' : 'No'] as [string, React.ReactNode],
    ] : []),
    ['Locked', tag.locked ? 'Yes' : 'No'],
    ...(tag.updated_at
      ? [['Last Updated', new Date(tag.updated_at).toLocaleString()] as [string, React.ReactNode]]
      : []
    ),
  ];

  return (
    <Dialog open={!!tag} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Tag Details</DialogTitle>
      <DialogContent sx={{ px: 1 }}>
        <Table size="small">
          <TableBody>
            {rows.map(([label, value]) => (
              <TableRow key={label as string}>
                <TableCell
                  sx={{ fontWeight: 600, width: '45%', borderBottom: 'none', py: 0.75, color: 'text.secondary', fontSize: '0.8rem' }}
                >
                  {label}
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.75, fontSize: '0.875rem' }}>
                  {value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}



// ─── Create / Edit dialog ─────────────────────────────────────────────────────

interface TagDialogProps {
  open: boolean;
  editTag: EmployeeTag | null;
  parentId: number;
  onClose: () => void;
  onSaved: (tag: EmployeeTag, keepOpen: boolean) => void;
}

function TagDialog({ open, editTag, parentId, onClose, onSaved }: TagDialogProps) {
  const isEdit = editTag !== null;
  const isRoot = isEdit ? editTag.parentId === 0 : parentId === 0;

  const navContainerRef = React.useRef<HTMLElement | null>(null);

  const [tagTitle, setTagTitle] = useState('');
  const [tagTitleBangla, setTagTitleBangla] = useState('');
  const [tagCode, setTagCode] = useState('');
  const [allowMulti, setAllowMulti] = useState(false);
  const [considerSub, setConsiderSub] = useState(false);
  const [multiLevelGrouping, setMultiLevelGrouping] = useState(false);
  const [concatTitles, setConcatTitles] = useState(false);
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [addAnother, setAddAnother] = useState(!isRoot);
  const titleInputRef = React.useRef<HTMLInputElement | null>(null);


  useEffect(() => {
    if (open) {
      if (editTag) {
        setTagTitle(editTag.tagTitle);
        setTagTitleBangla(editTag.tagTitleBangla ?? '');
        setTagCode(editTag.tagCode ?? '');
        setAllowMulti(editTag.allowMultiChildSelection);
        setConsiderSub(editTag.considerSubParentAsTag);
        setMultiLevelGrouping(editTag.multiLevelGrouping);
        setConcatTitles(editTag.concatTitles);
        setHistoryEnabled(editTag.historyEnabled);
      } else {
        setTagTitle('');
        setTagTitleBangla('');
        setTagCode('');
        setAllowMulti(false);
        setConsiderSub(false);
        setMultiLevelGrouping(false);
        setConcatTitles(false);
        setHistoryEnabled(false);
        // Default 'Add another' based on dialog type each time it opens
        setAddAnother(!isRoot);
      }
      setError(null);
      setFieldErrors({});
      // Focus the title field when the dialog opens
      setTimeout(() => titleInputRef.current?.focus(), 50);
    }
  }, [open, editTag, isRoot]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      let saved: EmployeeTag;
      if (isEdit) {
        const payload: PatchEmployeeTagPayload = {
          tagId: editTag.tagId,
          tagTitle: tagTitle || undefined,
          tagTitleBangla: tagTitleBangla || null,
          allowMultiChildSelection: isRoot ? allowMulti : undefined,
          considerSubParentAsTag: isRoot ? considerSub : undefined,
          multiLevelGrouping: isRoot ? multiLevelGrouping : undefined,
          concatTitles: isRoot ? concatTitles : undefined,
          historyEnabled: isRoot ? historyEnabled : undefined,
        };
        const resp = await apiService.patchEmployeeTag(payload);
        saved = resp.data;
      } else {
        const payload: CreateEmployeeTagPayload = {
          tagTitle,
          tagTitleBangla: tagTitleBangla || null,
          tagCode: isRoot ? (tagCode || null) : undefined,
          parentId,
          allowMultiChildSelection: isRoot ? allowMulti : undefined,
          considerSubParentAsTag: isRoot ? considerSub : undefined,
          multiLevelGrouping: isRoot ? multiLevelGrouping : undefined,
          concatTitles: isRoot ? concatTitles : undefined,
          historyEnabled: isRoot ? historyEnabled : undefined,
        };
        const resp = await apiService.createEmployeeTag(payload);
        saved = resp.data;
      }
      onSaved(saved, !isEdit && addAnother);
      if (!isEdit && addAnother) {
        setTagTitle('');
        setTagTitleBangla('');
        setTagCode('');
        // Keep checkboxes as they are or reset? User said "resets", so let's reset.
        setAllowMulti(false);
        setConsiderSub(false);
        setMultiLevelGrouping(false);
        setConcatTitles(false);
        setHistoryEnabled(false);
        setFieldErrors({});
        setError(null);
      } else {
        onClose();
      }
    } catch (err: any) {
      if (err?.jsonMessage) {
        setFieldErrors(err.jsonMessage);
      } else {
        setError(err?.message ?? 'An error occurred');
      }
    } finally {
      setSaving(false);
      // Extra robust focus attempt after the save button is potentially re-enabled
      if (!isEdit && addAnother) {
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
            // Ensure any previous selection is cleared and we are ready to type
            titleInputRef.current.setSelectionRange(0, 0);
          }
        }, 100);
      }
    }
  }

  const title = isEdit ? 'Edit Tag' : parentId === 0 ? 'Create Category' : 'Add Child Tag';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        ref: navContainerRef
      }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <NavTextField
              inputRef={titleInputRef}
              label="Tag Title"
              value={tagTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagTitle(e.target.value)}
              error={!!fieldErrors['tagTitle']}
              helperText={fieldErrors['tagTitle']}
              fullWidth
              required
              size="small"
            />
            <NavBanglaPhoneticInput
              label="Tag Title (Bangla)"
              value={tagTitleBangla}
              onChange={setTagTitleBangla}
              fullWidth
              size="small"
            />
            {isRoot && !isEdit && (
              <NavTextField
                label="Tag Code"
                value={tagCode}
                onChange={(e) => setTagCode(e.target.value)}
                error={!!fieldErrors['tagCode']}
                helperText={fieldErrors['tagCode'] ?? 'Unique identifier, e.g. DEPARTMENT'}
                fullWidth
                required
                size="small"
              />
            )}
            {isRoot && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 0.5 }}>
                <FormControlLabel
                  control={<NavCheckbox checked={allowMulti} onChange={(e) => setAllowMulti(e.target.checked)} disabled={editTag?.locked} />}
                  label="Allow multi-child selection"
                />
                <FormControlLabel
                  control={<NavCheckbox checked={multiLevelGrouping} onChange={(e) => setMultiLevelGrouping(e.target.checked)} disabled={editTag?.locked} />}
                  label="Multi-level grouping"
                />
                <FormControlLabel
                  control={<NavCheckbox checked={considerSub} onChange={(e) => setConsiderSub(e.target.checked)} disabled={editTag?.locked} />}
                  label="Consider sub-parent as tag"
                />
                <FormControlLabel
                  control={<NavCheckbox checked={concatTitles} onChange={(e) => setConcatTitles(e.target.checked)} />}
                  label="Concat titles"
                />
                <FormControlLabel
                  control={<NavCheckbox checked={historyEnabled} onChange={(e) => setHistoryEnabled(e.target.checked)} />}
                  label="Keep history"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          {!isEdit ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={addAnother}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddAnother(e.target.checked)}
                />
              }
              label="Add another"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          ) : <Box />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <NavButton navSkip={false} onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={18} /> : isEdit ? 'Save' : 'Create'}
            </NavButton>
          </Box>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── History dialog ───────────────────────────────────────────────────────────

interface HistoryDialogProps {
  tag: EmployeeTag | null;
  onClose: () => void;
  onUpdated: (tag: EmployeeTag) => void;
}

function HistoryDialog({ tag, onClose, onUpdated }: HistoryDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keepHistory, setKeepHistory] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  useEffect(() => {
    if (tag) {
      setKeepHistory(tag.historyEnabled);
      setError(null);
      setSaving(false);
      setConfirmation('');
    }
  }, [tag]);

  const changed = keepHistory !== (tag?.historyEnabled ?? false);
  const disabling = tag?.historyEnabled && !keepHistory;
  const confirmationRequired = disabling && confirmation !== (tag?.tagCode ?? '');
  const applyDisabled = !changed || confirmationRequired || saving;

  async function handleApply() {
    if (!tag || tag.updated_at == null) return;
    setSaving(true);
    setError(null);
    try {
      const payload: SetTagHistoryPayload = { historyEnabled: keepHistory, updated_at: tag.updated_at };
      const response = await apiService.setEmployeeTagHistory(tag.tagId, payload);
      onUpdated(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update history setting');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!tag} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>History Tracking</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControlLabel
          control={<Switch checked={keepHistory} onChange={(e) => setKeepHistory(e.target.checked)} disabled={saving} />}
          label="Keep History"
        />
        {disabling && (
          <>
            <DialogContentText sx={{ mt: 1, color: 'error.main', fontWeight: 500, fontSize: '0.8rem' }}>
              Disabling history will flatten all employee records for this tag category to their most recent value. This cannot be undone.
            </DialogContentText>
            <DialogContentText sx={{ mt: 1, fontSize: '0.8rem' }}>
              Type <strong>{tag?.tagCode}</strong> to confirm:
            </DialogContentText>
            <TextField
              size="small"
              fullWidth
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={tag?.tagCode ?? ''}
              sx={{ mt: 1 }}
              disabled={saving}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" color={disabling ? 'error' : 'primary'} disabled={applyDisabled}>
          {saving ? <CircularProgress size={18} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────

function DeleteDialog({
  tag,
  allTags,
  onClose,
  onDeleted,
}: {
  tag: EmployeeTag | null;
  allTags: EmployeeTag[];
  onClose: () => void;
  onDeleted: (tagId: number) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descendantCount = tag
    ? allTags.filter((t) => t.tagId !== tag.tagId && t.dna.includes(tag.tagId)).length
    : 0;

  async function handleDelete() {
    if (!tag) return;
    setDeleting(true);
    setError(null);
    try {
      await apiService.deleteEmployeeTag(tag.tagId);
      onDeleted(tag.tagId);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete tag');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={!!tag} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Tag</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Delete <strong>{tag?.tagTitle}</strong>?
          {descendantCount > 0 && (
            <> This will also delete <strong>{descendantCount}</strong> descendant tag{descendantCount !== 1 ? 's' : ''}.
            </>
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
          {deleting ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Unlock Tag dialog ────────────────────────────────────────────────────────

interface UnlockTagDialogProps {
  tag: EmployeeTag | null;
  allTags: EmployeeTag[];
  onClose: () => void;
  onUnlocked: (tag: EmployeeTag) => void;
}

function UnlockTagDialog({ tag, allTags, onClose, onUnlocked }: UnlockTagDialogProps) {
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');

  useEffect(() => {
    setError(null); setUnlocking(false); setConfirmation('');
  }, [tag]);

  const isRoot = tag?.parentId === 0;
  const isLeaf = tag ? (tag.parentId !== 0 && !allTags.some((t) => t.parentId === tag.tagId)) : false;
  const rootTag = tag ? (isRoot ? tag : allTags.find((t) => t.tagId === tag.dna[1]) ?? null) : null;

  // For root tags prefer tagCode; fall back to tagTitle so the target is never empty.
  const confirmationTarget = isRoot
    ? (tag?.tagCode || tag?.tagTitle || '')
    : (tag?.tagTitle || tag?.tagCode || '');
  // Require a non-empty match — guards against the empty-string auto-confirm edge case.
  const confirmed = confirmationTarget.length > 0 && confirmation === confirmationTarget;

  async function handleConfirm() {
    if (!tag) return;
    setUnlocking(true); setError(null);
    try {
      const payload: UnlockEmployeeTagPayload = (isRoot && tag.updated_at != null)
        ? { updated_at: tag.updated_at }
        : {};
      const response = await apiService.unlockEmployeeTag(tag.tagId, payload);
      onUnlocked(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to unlock tag');
    } finally {
      setUnlocking(false);
    }
  }

  let warningText = '';
  if (isRoot) {
    warningText = `This action is irreversible. It will permanently delete all "${tag?.tagCode}" data from every employee record.`;
  } else if (isLeaf) {
    warningText = rootTag?.historyEnabled
      ? 'This action is irreversible. It will remove all dated history entries for this tag from every employee record where it is assigned.'
      : 'This action is irreversible. It will permanently remove this tag and its value from every employee record where it is assigned.';
  } else {
    warningText = 'This action is irreversible. It will remove this tag and all its nested child assignments from every employee record.';
  }

  return (
    <Dialog open={!!tag} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Unlock Tag</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>
          Unlock <strong>{tag?.tagTitle}</strong>? Once unlocked, the tag can be edited or deleted.
        </DialogContentText>
        <DialogContentText sx={{ mt: 1, color: 'error.main', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
          <WarningAmberIcon sx={{ fontSize: '0.9rem' }} />
          {warningText}
        </DialogContentText>
        <DialogContentText sx={{ mt: 2 }}>
          Type <strong>{confirmationTarget}</strong> to confirm:
        </DialogContentText>
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          placeholder={confirmationTarget}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          onPaste={(e) => e.preventDefault()}
          disabled={unlocking}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={unlocking}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={!confirmed || unlocking}>
          {unlocking ? <CircularProgress size={18} /> : 'Unlock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EmployeeTags() {
  const dispatch = useAppDispatch();
  const { tags, isLoading, error } = useAppSelector((s) => s.employeeTags);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTag, setEditTag] = useState<EmployeeTag | null>(null);
  const [newParentId, setNewParentId] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeTag | null>(null);
  const [infoTag, setInfoTag] = useState<EmployeeTag | null>(null);
  const [historyTarget, setHistoryTarget] = useState<EmployeeTag | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<EmployeeTag | null>(null);
  const [unlocking] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const treeRef = useRef<TreeApi<TagNode>>(null);
  const initialExpansionDone = useRef(false);
  const [treeHeight, setTreeHeight] = useState(600);

  // Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [menuTag, setMenuTag] = useState<EmployeeTag | null>(null);
  const menuOpen = Boolean(anchorEl) || Boolean(contextMenuPosition);
  
  const menuListRef = useRef<HTMLUListElement>(null);
  const treeWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!treeWrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setTreeHeight(entries[0].contentRect.height);
    });
    observer.observe(treeWrapperRef.current);
    return () => observer.disconnect();
  }, [tags.length]);

  const isLeafNode = (tagId: number | undefined) => {
    if (!tagId) return false;
    const target = tags.find((t) => t.tagId === tagId);
    if (!target) return false;
    if (target.parentId === 0) return false;
    return !tags.some((t) => t.parentId === tagId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setContextMenuPosition(null);
    setMenuTag(null);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleGlobalClick = (e: MouseEvent) => {
      // Allow seamless right-click menu teleporting exclusively within the tree container
      if (e.button === 2 && treeWrapperRef.current?.contains(e.target as Node)) return;

      if (menuListRef.current && menuListRef.current.contains(e.target as Node)) return;
      if (anchorEl && anchorEl.contains(e.target as Node)) return;
      
      handleMenuClose();
    };

    document.addEventListener('pointerdown', handleGlobalClick, true);
    return () => {
      document.removeEventListener('pointerdown', handleGlobalClick, true);
    };
  }, [menuOpen, anchorEl]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, tag: EmployeeTag) => {
    event.stopPropagation();
    if (menuOpen && anchorEl === event.currentTarget) {
      setAnchorEl(null);
      setMenuTag(null);
      return;
    }
    setContextMenuPosition(null);
    setAnchorEl(event.currentTarget);
    setMenuTag(tag);
  };
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, tag: EmployeeTag) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
    setContextMenuPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setMenuTag(tag);
  };



  // Expand root tags on first load
  useEffect(() => {
    if (tags.length > 0 && !initialExpansionDone.current) {
      initialExpansionDone.current = true;
      setTimeout(() => {
        tags.forEach(t => {
           treeRef.current?.open(String(t.tagId));
        });
      }, 50);
    }
  }, [tags]);

  function openCreate(parentId = 0) {
    setEditTag(null);
    setNewParentId(parentId);
    setDialogOpen(true);
  }

  function openEdit(tag: EmployeeTag) {
    setEditTag(tag);
    setNewParentId(tag.parentId);
    setDialogOpen(true);
  }

  function handleSaved(tag: EmployeeTag, keepOpen = false) {
    if (editTag) {
      dispatch(replaceEmployeeTag(tag));
      dispatch(showNotification({ message: 'Tag updated', severity: 'success' }));
    } else {
      dispatch(addEmployeeTag(tag));
      dispatch(showNotification({ message: 'Tag created', severity: 'success' }));
      if (tag.parentId === 0) {
        // New root category — switch to it only if user had a specific category selected
        if (selectedCategory !== 'all') {
          setSelectedCategory(String(tag.tagId));
        }
      } else {
        // Expand all ancestors so the new tag is immediately visible
        const ancestorIds = tag.dna.slice(1, -1).map((id) => String(id));
        if (ancestorIds.length > 0) {
          ancestorIds.forEach(id => treeRef.current?.open(id));
        }
        setTimeout(() => {
          if (!keepOpen) {
            treeRef.current?.scrollTo(String(tag.tagId));
            treeRef.current?.focus(String(tag.tagId));
          } else {
            treeRef.current?.scrollTo(String(tag.tagId));
          }
        }, 150);
      }
    }
    dispatch(fetchEmployeeTags());
  }

  function handleDeleted(tagId: number) {
    dispatch(removeEmployeeTag(tagId));
    dispatch(showNotification({ message: 'Tag deleted', severity: 'success' }));
  }

  function handleHistoryUpdated(tag: EmployeeTag) {
    dispatch(replaceEmployeeTag(tag));
    dispatch(showNotification({ message: `History tracking ${tag.historyEnabled ? 'enabled' : 'disabled'} for "${tag.tagCode}"`, severity: 'success' }));
  }

  function handleUnlocked(tag: EmployeeTag) {
    dispatch(replaceEmployeeTag(tag));
    dispatch(showNotification({ message: 'Tag unlocked', severity: 'success' }));
  }

  async function handleTreeMove({ dragIds, parentId }: { dragIds: string[], parentId: string | null }) {
    const tagId = Number(dragIds[0]);
    const newParentId = parentId ? Number(parentId) : 0;

    const draggingTag = tags.find((t) => t.tagId === tagId);
    if (!draggingTag) return;

    if (newParentId === 0 && draggingTag.parentId !== 0) {
      return; // silently abort
    }
    // Abort if the drop would make a root tag into a child tag
    if (newParentId !== 0 && draggingTag.parentId === 0) {
      return; // silently abort
    }

    try {
      const resp = await apiService.patchEmployeeTag({ tagId, parentId: newParentId });
      dispatch(fetchEmployeeTags());
      dispatch(showNotification({ message: 'Tag moved successfully', severity: 'success' }));
      if (resp.data.parentId !== 0) {
        treeRef.current?.open(String(resp.data.parentId));
      }
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to move tag', severity: 'error' }));
    }
  }

  const tree = buildTree(tags);
  const rootCategories = tree.map((n) => ({ id: n.id, name: n.name }));
  const visibleTree = selectedCategory === 'all'
    ? tree
    : tree.filter((n) => n.id === selectedCategory);

  const cb: TreeCallbacks = {
    onUnlock: setUnlockTarget,
    onInfo: setInfoTag,
    onMenuOpen: handleMenuOpen,
    onContextMenu: handleContextMenu,
    onHistoryEdit: setHistoryTarget,
    unlocking,
  };

  return (
    <DashboardLayout>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link underline="hover" color="inherit" href="#">Employee Entry</Link>
        <Link underline="hover" color="inherit" href="#">Configuration</Link>
        <Typography color="text.primary">Employee Tags</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" fontWeight={600}>Employee Tags</Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={() => dispatch(fetchEmployeeTags())} disabled={isLoading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(0)}>
          New Category
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tree.length > 0 && (
        <FormControl size="small" sx={{ mb: 1.5, minWidth: 220, bgcolor: 'white', borderRadius: 1 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {rootCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Paper variant="outlined" sx={{ p: 1, position: 'relative' }}>
        {isLoading && tree.length > 0 && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderTopLeftRadius: 4, borderTopRightRadius: 4 }} />
        )}
        {isLoading && tree.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tree.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No employee tags found. Create a root tag to get started.</Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={treeWrapperRef}
              sx={{ width: '100%', height: 'calc(100vh - 270px)', minHeight: 400 }}
              onContextMenu={(e) => {
                e.preventDefault();
                handleMenuClose();
              }}
            >
              <Tree
                ref={treeRef}
                data={visibleTree}
                width="100%"
                height={treeHeight}
                rowHeight={40}
                indent={32}
                onMove={handleTreeMove as any}
                disableDrag={(node: any) => !!node.data?.tag?.locked}
                disableDrop={({ parentNode, dragNodes }: any) => {
                  const targetTag = parentNode?.data?.tag;
                  if (!targetTag) {
                    return dragNodes.some((node: any) => node.data?.tag?.parentId !== 0);
                  }
                  // Dropping under a target node — block if dragged node is a Root Category
                  if (dragNodes.some((node: any) => node.data?.tag?.parentId === 0)) {
                    return true;
                  }
                  if (targetTag.locked && isLeafNode(targetTag.tagId)) {
                    return true;
                  }
                  // Block if multiLevelGrouping is false and target is a child tag
                  if (targetTag.parentId !== 0) {
                    const rootTag = tags.find((t: any) => t.tagId === targetTag.dna[1]);
                    if (rootTag && !rootTag.multiLevelGrouping) {
                      return true;
                    }
                  }
                  return false;
                }}
                renderCursor={(props: any) => (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: props.left,
                      top: props.top,
                      width: props.width,
                      height: 2,
                      bgcolor: 'primary.main',
                      zIndex: 1,
                    }}
                  />
                )}
                {...{ callbacks: cb } as any}
              >
                {TagNodeRenderer}
              </Tree>
            </Box>
          </>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        anchorReference={contextMenuPosition ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={contextMenuPosition !== null ? contextMenuPosition : undefined}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: contextMenuPosition ? 'left' : 'right' }}
        sx={contextMenuPosition ? { pointerEvents: 'none' } : {}}
        PaperProps={{ sx: { pointerEvents: 'auto' } }}
        hideBackdrop={!!contextMenuPosition}
        disableScrollLock={!!contextMenuPosition}
        MenuListProps={{ ref: menuListRef }}
      >
        <MenuItem
           disabled={
             (menuTag?.locked && isLeafNode(menuTag.tagId)) ||
             (menuTag != null && menuTag.parentId !== 0 && tags.find(t => t.tagId === menuTag.dna[1])?.multiLevelGrouping === false)
           }
           onClick={() => {
             if (menuTag) openCreate(menuTag.tagId);
             handleMenuClose();
           }}
        >
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add child tag</ListItemText>
        </MenuItem>
        <MenuItem
           onClick={() => {
             if (menuTag) openEdit(menuTag);
             handleMenuClose();
           }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
           disabled={menuTag?.locked}
           onClick={() => {
             if (menuTag) setDeleteTarget(menuTag);
             handleMenuClose();
           }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color={menuTag?.locked ? "inherit" : "error"} /></ListItemIcon>
          <ListItemText sx={{ color: menuTag?.locked ? 'text.disabled' : 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <TagDialog
        open={dialogOpen}
        editTag={editTag}
        parentId={newParentId}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <DeleteDialog
        tag={deleteTarget}
        allTags={tags}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      <InfoModal tag={infoTag} onClose={() => setInfoTag(null)} />

      <HistoryDialog
        tag={historyTarget}
        onClose={() => setHistoryTarget(null)}
        onUpdated={handleHistoryUpdated}
      />

      <UnlockTagDialog
        tag={unlockTarget}
        allTags={tags}
        onClose={() => setUnlockTarget(null)}
        onUnlocked={handleUnlocked}
      />

    </DashboardLayout>
  );
}
